export const SYSTEM_FONTS = [
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Impact',
  'Courier New',
  'Lucida Console',
  'Comic Sans MS',
  'Palatino',
  'Book Antiqua',
  'Bookman Old Style',
  'Century Gothic',
  'Lucida Sans Unicode',
]

export const GOOGLE_FONTS_POPULAR = [
  'Inter',
  'Open Sans',
  'Roboto',
  'Montserrat',
  'Lato',
  'Poppins',
  'Raleway',
  'Nunito Sans',
  'Playfair Display',
  'Merriweather',
  'Ubuntu',
  'Source Sans Pro',
  'Oswald',
  'PT Sans',
  'Mukti',
  'Libre Baskerville',
  'Dancing Script',
  'Pacifico',
  'Quicksand',
  'Titillium Web',
  'Lora'
]

class GoogleFontsManager {
  constructor() {
    this.loadedFonts = new Map()
    this.loadingPromises = new Map()
    this.styleElements = new Map()
    this.fontConfiguration = null
    this.cacheConfiguration = { fontCacheDuration: 86400 }

    this.isTablet = this.detectTabletEnvironment()
    this.maxConcurrentLoads = this.isTablet ? 2 : 4
    this.loadTimeout = this.isTablet ? 10000 : 5000
    this.fontLoadQueue = []
    this.currentlyLoading = 0

    this.performanceMetrics = {
      fontsLoaded: 0,
      totalLoadTime: 0,
      failedLoads: 0
    }
  }

  detectTabletEnvironment() {
    const userAgent = navigator.userAgent
    const isAndroidTablet = /Android.*(?!Mobile)/.test(userAgent)
    const isIPadTablet = /iPad/.test(userAgent)
    const isWebView = /wv|WebView/.test(userAgent)
    const hasAndroidDevice = typeof window.AndroidDevice !== 'undefined'

    return isAndroidTablet || isIPadTablet || isWebView || hasAndroidDevice
  }

  static getInstance() {
    if (!GoogleFontsManager.instance) {
      GoogleFontsManager.instance = new GoogleFontsManager()
    }
    return GoogleFontsManager.instance
  }

  setConfiguration(config) {
    this.fontConfiguration = config.fontSettings
    this.cacheConfiguration = config.performance?.cacheConfiguration || { fontCacheDuration: 86400 }

    if (config.fontSettings?.googleFontsEnabled) {
      this.preloadPrimaryFonts()
    }
  }

  async preloadPrimaryFonts() {
    if (!this.fontConfiguration) return

    const fontsToLoad = [
      this.fontConfiguration.primary,
      this.fontConfiguration.headingFont,
      this.fontConfiguration.bodyFont,
      this.fontConfiguration.buttonFont
    ].filter(Boolean)

    const uniqueFonts = [...new Set(fontsToLoad)]

    try {
      await this.preloadFonts(uniqueFonts, ['400', '500', '600', '700'])
    } catch (error) {
      console.warn('Font preloading failed:', error)
    }
  }

  isFontAvailable(fontName) {
    if (SYSTEM_FONTS.includes(fontName)) {
      return true
    }

    const state = this.loadedFonts.get(fontName)
    return state?.loaded === true
  }

  getFontState(fontName) {
    if (SYSTEM_FONTS.includes(fontName)) {
      return {
        name: fontName,
        loaded: true,
        loading: false,
        error: false,
        timestamp: Date.now(),
      }
    }

    return this.loadedFonts.get(fontName) || null
  }

  async loadFont(fontName, weights = ['400']) {
    if (SYSTEM_FONTS.includes(fontName)) {
      return true
    }

    if (!this.fontConfiguration?.googleFontsEnabled) {
      return false
    }

    const existingPromise = this.loadingPromises.get(fontName)
    if (existingPromise) {
      return existingPromise
    }

    const existingState = this.loadedFonts.get(fontName)
    if (existingState?.loaded) {
      return true
    }

    if (this.isTablet && this.currentlyLoading >= this.maxConcurrentLoads) {
      return this.queueFontLoad(fontName, weights)
    }

    const loadingPromise = this.performFontLoad(fontName, weights)
    this.loadingPromises.set(fontName, loadingPromise)

    try {
      const result = await loadingPromise
      return result
    } finally {
      this.loadingPromises.delete(fontName)
      this.processNextInQueue()
    }
  }

  async queueFontLoad(fontName, weights) {
    return new Promise((resolve, reject) => {
      this.fontLoadQueue.push({
        fontName,
        weights,
        resolve,
        reject,
        timestamp: Date.now()
      })
    })
  }

  async processNextInQueue() {
    if (this.fontLoadQueue.length === 0 || this.currentlyLoading >= this.maxConcurrentLoads) {
      return
    }

    const { fontName, weights, resolve, reject } = this.fontLoadQueue.shift()

    try {
      const loadingPromise = this.performFontLoad(fontName, weights)
      this.loadingPromises.set(fontName, loadingPromise)
      const result = await loadingPromise
      resolve(result)
    } catch (error) {
      reject(error)
    } finally {
      this.loadingPromises.delete(fontName)
      setTimeout(() => this.processNextInQueue(), 100)
    }
  }

  async performFontLoad(fontName, weights) {
    const startTime = Date.now()
    this.currentlyLoading++

    try {
      this.loadedFonts.set(fontName, {
        name: fontName,
        loaded: false,
        loading: true,
        error: false,
        timestamp: Date.now(),
      })

      const weightsParam = weights.join(';')
      const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
        fontName.replace(/ /g, '+')
      )}:wght@${weightsParam}&display=swap`

      await this.loadFontCSS(fontName, fontUrl)

      const fontLoadTimeout = this.isTablet ? 5000 : 3000
      await this.waitForFontLoad(fontName, fontLoadTimeout)

      this.loadedFonts.set(fontName, {
        name: fontName,
        loaded: true,
        loading: false,
        error: false,
        timestamp: Date.now(),
      })

      const loadTime = Date.now() - startTime
      this.performanceMetrics.fontsLoaded++
      this.performanceMetrics.totalLoadTime += loadTime

      if (this.isTablet) {
        console.log(`[Tablet] Font loaded: ${fontName} in ${loadTime}ms`)
      }

      return true
    } catch (error) {
      console.warn(`Failed to load Google font: ${fontName}`, error)
      this.performanceMetrics.failedLoads++

      this.loadedFonts.set(fontName, {
        name: fontName,
        loaded: false,
        loading: false,
        error: true,
        timestamp: Date.now(),
      })

      return false
    } finally {
      this.currentlyLoading--
    }
  }

  async loadFontCSS(fontName, fontUrl) {
    return new Promise((resolve, reject) => {
      const existingStyle = this.styleElements.get(fontName)
      if (existingStyle) {
        existingStyle.remove()
      }

      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = fontUrl
      link.crossOrigin = 'anonymous'

      if (this.cacheConfiguration.fontCacheDuration) {
        link.setAttribute('data-cache-duration', this.cacheConfiguration.fontCacheDuration)
      }

      link.onload = () => resolve()
      link.onerror = () => reject(new Error(`Failed to load font CSS: ${fontUrl}`))

      document.head.appendChild(link)
      this.styleElements.set(fontName, link)

      setTimeout(() => {
        reject(new Error(`Font CSS load timeout: ${fontName}`))
      }, this.loadTimeout)
    })
  }

  async waitForFontLoad(fontName, timeout = 3000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now()

      const checkFont = () => {
        if (document.fonts && document.fonts.check(`1em "${fontName}"`)) {
          resolve()
          return
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error(`Font load timeout: ${fontName}`))
          return
        }

        setTimeout(checkFont, 100)
      }

      checkFont()
    })
  }

  async preloadFonts(fonts, weights = ['400']) {
    const results = new Map()

    const batchSize = 2
    for (let i = 0; i < fonts.length; i += batchSize) {
      const batch = fonts.slice(i, i + batchSize)

      const batchResults = await Promise.allSettled(
        batch.map(font => this.loadFont(font, weights))
      )

      batchResults.forEach((result, index) => {
        const fontName = batch[index]
        results.set(fontName, result.status === 'fulfilled' && result.value)
      })

      if (i + batchSize < fonts.length) {
        await new Promise(resolve => setTimeout(resolve, 200))
      }
    }

    return results
  }

  getFontFamily(fontName) {
    if (!fontName || !this.fontConfiguration) {
      return this.fontConfiguration?.systemFontFallback ||
        'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }

    const isSystemFont = SYSTEM_FONTS.includes(fontName)
    const state = this.loadedFonts.get(fontName)

    const baseFallback = this.fontConfiguration.systemFontFallback ||
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

    if (isSystemFont || (this.fontConfiguration.googleFontsEnabled && state?.loaded)) {
      return `"${fontName}", ${baseFallback}`
    }

    return baseFallback
  }

  applyFontConfiguration() {
    if (!this.fontConfiguration) return

    const root = document.documentElement

    root.style.setProperty('--font-family-primary', this.getFontFamily(this.fontConfiguration.primary))
    root.style.setProperty('--font-family-heading', this.getFontFamily(this.fontConfiguration.headingFont || this.fontConfiguration.primary))
    root.style.setProperty('--font-family-body', this.getFontFamily(this.fontConfiguration.bodyFont || this.fontConfiguration.primary))
    root.style.setProperty('--font-family-button', this.getFontFamily(this.fontConfiguration.buttonFont || this.fontConfiguration.primary))
    root.style.setProperty('--font-family-fallback', this.fontConfiguration.systemFontFallback)

    document.body.style.fontFamily = 'var(--font-family-body)'
  }

  getPerformanceMetrics() {
    const avgLoadTime = this.performanceMetrics.fontsLoaded > 0
      ? this.performanceMetrics.totalLoadTime / this.performanceMetrics.fontsLoaded
      : 0

    return {
      ...this.performanceMetrics,
      averageLoadTime: Math.round(avgLoadTime),
      isTablet: this.isTablet,
      currentlyLoading: this.currentlyLoading,
      queueLength: this.fontLoadQueue.length,
      loadedFontsCount: this.loadedFonts.size,
      cacheConfiguration: this.cacheConfiguration
    }
  }

  logPerformanceStats() {
    if (this.isTablet) {
      const metrics = this.getPerformanceMetrics()
      console.log('[Tablet Font Performance]', JSON.stringify(metrics, null, 2))
    }
  }

  cleanup() {
    this.styleElements.forEach(style => style.remove())
    this.styleElements.clear()
    this.loadedFonts.clear()
    this.loadingPromises.clear()
    this.fontLoadQueue.length = 0
    this.currentlyLoading = 0
  }
}

export const googleFonts = GoogleFontsManager.getInstance()

export const isFontLoaded = (fontName) => {
  return googleFonts.isFontAvailable(fontName)
}

export const loadFont = (fontName, weights) => {
  return googleFonts.loadFont(fontName, weights)
}

export const getFontFamily = (fontName) => {
  return googleFonts.getFontFamily(fontName)
}

export const preloadFonts = (fonts, weights) => {
  return googleFonts.preloadFonts(fonts, weights)
}